// 🔧 auction_results.js
// ✅ Purpose: Admin-facing Silent Auction results page
// ✅ Features:
// - Loads auction items from /events/{eventId}/auctionItems
// - Shows closed, open, and draft auction items with winner/payment info
// - Highlights winning bidder and winning bid amount
// - Allows admin/manager to update payment status
// - Allows admin/manager to mark item as picked up
// - Supports future payment workflows with Stripe/Square/PayPal/manual payments
// - Provides export-friendly table layout
// - Respects auctionEnabled setting
// - Uses top-of-page success/error notifications for 20 seconds
// - Includes console logging for debugging
// - Fully block-commented for clarity and future devs

import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { db, functions } from "../../firebase";
import { httpsCallable } from "firebase/functions";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";
import {
  hasPurchasedAddOn,
} from "../../utils/event_addons";
import AddonRequired from "../../components/AddonRequired";

const AuctionResults = () => {

    const {
    eventId,
    eventLoading,
    ready,
    eventDoc,
    eventslug,
    } = useEventContext({ redirectIfNotFound: true });

    const auctionEnabled = hasPurchasedAddOn(
      eventDoc,
      "auction"
    );
    const [auctionItems, setAuctionItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [savingItemId, setSavingItemId] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    const [status, setStatus] = useState("");
    const [error, setError] = useState("");

  useEffect(() => {
    if (!ready || !eventId) return;

    loadAuctionResults();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, eventId]);

  const showSuccess = (message) => {
    setStatus(message);
    setError("");

    setTimeout(() => {
      setStatus("");
    }, 20000);
  };

  const showError = (message) => {
    setError(message);
    setStatus("");

    setTimeout(() => {
      setError("");
    }, 20000);
  };

  const formatMoney = (value) => {
    return Number(value || 0).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  };

  const loadAuctionResults = async () => {
    try {
      setLoading(true);

      logger.log("🔎 Loading auction results", {
        value: eventId,
      });

      const auctionRef = collection(db, "events", eventId, "auctionItems");

      const snap = await getDocs(auctionRef);

      const loadedItems = await Promise.all(snap.docs.map(async (auctionDoc) => {
        const allocationsSnap = await getDocs(collection(auctionDoc.ref, "allocations"));
        return {
          id: auctionDoc.id,
          ...auctionDoc.data(),
          allocations: allocationsSnap.docs
            .map((allocationDoc) => ({ id: allocationDoc.id, ...allocationDoc.data() }))
            .sort((a, b) => Number(a.rank || 0) - Number(b.rank || 0)),
        };
      }));
      loadedItems
        .sort((a, b) =>
          String(a.title || a.linkedEntryLabel || "")
            .localeCompare(String(b.title || b.linkedEntryLabel || ""))
        );

      setAuctionItems(loadedItems);

      logger.log("✅ Auction results loaded", {
        value: loadedItems.length,
      });
    } catch (err) {
      logger.error("❌ Failed to load auction results", {
        value: err,
      });

      showError("❌ Failed to load auction results.");
    } finally {
      setLoading(false);
    }
  };

  const offerToNextBidder = async (item) => {
  try {
    const confirmed = window.confirm(
      `Offer "${item.title || "this item"}" to the next highest bidder?`
    );

    if (!confirmed) {
      return;
    }

    setSavingItemId(item.id);

    const offerFn = httpsCallable(
      functions,
      "offerAuctionItemToNextBidder"
    );

    const result = await offerFn({
      eventId,
      auctionItemId: item.id,
    });

    const nextBidder =
      result?.data?.nextBidderName ||
      result?.data?.nextBidderEmail ||
      "Next Bidder";

    showSuccess(
      `✅ Item offered to ${nextBidder}. You can now send a payment link.`
    );

    await loadAuctionResults();
  } catch (err) {
    logger.error("❌ Failed to offer item to next bidder", {
      value: err,
    });

    showError(
      err?.message ||
        "❌ Failed to offer item to next bidder."
    );
  } finally {
    setSavingItemId("");
  }
};

  const sendAuctionPaymentLink = async (item, allocation = null) => {
    try {
      const savingKey = allocation ? `${item.id}:${allocation.id}` : item.id;
      setSavingItemId(savingKey);
      setStatus("");
      setError("");

      if (!item?.id) {
        showError("❌ Auction item was not found.");
        return;
      }

      if (item.status !== "closed") {
        showError("❌ Close the auction item before sending a payment link.");
        return;
      }

      const winnerEmail = allocation?.bidderEmail || item.currentWinnerEmail;
      const winningAmount = allocation?.amount || item.currentBid;
      if (!winnerEmail) {
        showError("❌ Winning bidder email is required before sending payment.");
        return;
      }

      if (Number(winningAmount || 0) <= 0) {
        showError("❌ Auction item does not have a winning bid.");
        return;
      }

      const createAuctionCheckoutSession = httpsCallable(
        functions,
        "createAuctionCheckoutSession"
      );

      logger.log("💳 Creating auction payment checkout session", {
        value: {
          eventId,
          auctionItemId: item.id,
        },
      });

      await createAuctionCheckoutSession({
        eventId,
        auctionItemId: item.id,
        auctionAllocationId: allocation?.id || "",
      });

      showSuccess("✅ Auction payment link sent to the winner.");

      await loadAuctionResults();
    } catch (err) {
      logger.error("❌ Failed to send auction payment link", {
        value: err,
      });

      showError(
        err?.message ||
          "❌ Failed to send auction payment link."
      );
    } finally {
      setSavingItemId("");
    }
  };

  const updatePaymentStatus = async (itemId, paymentStatus, allocationId = "") => {
    try {
      setSavingItemId(allocationId ? `${itemId}:${allocationId}` : itemId);

      const updatePayload = {
        paymentStatus,
        updatedAt: serverTimestamp(),
      };

      if (paymentStatus === "paid") {
        updatePayload.paidAt = serverTimestamp();
        updatePayload.pickupStatus = "awaiting_pickup";
      }

      if (paymentStatus !== "paid") {
        updatePayload.pickupStatus = "awaiting_payment";
        updatePayload.pickedUp = false;
        updatePayload.pickedUpAt = null;
      }

      logger.log("💳 Updating auction payment status", {
        value: {
          itemId,
          paymentStatus,
        },
      });

      const manageAuctionItem = httpsCallable(functions, "manageAuctionItem");
      await manageAuctionItem({ eventId, auctionItemId: itemId, auctionAllocationId: allocationId, action: "set_payment_status", paymentStatus });

      showSuccess("✅ Payment status updated.");

      await loadAuctionResults();
    } catch (err) {
      logger.error("❌ Failed to update payment status", {
        value: err,
      });

      showError("❌ Failed to update payment status.");
    } finally {
      setSavingItemId("");
    }
  };

  const sendPickupReminder = async (item) => {
    try {
      setSavingItemId(item.id);
      setStatus("");
      setError("");

      const sendReminder = httpsCallable(
        functions,
        "sendAuctionPickupReminder"
      );

      await sendReminder({
        eventId,
        auctionItemId: item.id,
      });

      showSuccess("✅ Pickup reminder sent to the auction winner.");

      await loadAuctionResults();
    } catch (err) {
      logger.error("❌ Failed to send pickup reminder", {
        value: err,
      });

      showError(
        err?.message ||
          "❌ Failed to send pickup reminder."
      );
    } finally {
      setSavingItemId("");
    }
  };

  const togglePickedUp = async (item, allocation = null) => {
    try {
      setSavingItemId(allocation ? `${item.id}:${allocation.id}` : item.id);

      const currentlyPickedUp =
        (allocation?.pickupStatus || item.pickupStatus) === "picked_up" ||
        (allocation?.pickedUp ?? item.pickedUp) === true;

      const nextPickedUp = !currentlyPickedUp;

      const updatePayload = {
        pickedUp: nextPickedUp,
        pickupStatus: nextPickedUp
          ? "picked_up"
          : (allocation?.paymentStatus || item.paymentStatus) === "paid"
          ? "awaiting_pickup"
          : "awaiting_payment",
        updatedAt: serverTimestamp(),
      };

      if (nextPickedUp) {
        updatePayload.pickedUpAt = serverTimestamp();
      } else {
        updatePayload.pickedUpAt = null;
      }

      logger.log("📦 Updating auction pickup status", {
        value: {
          itemId: item.id,
          pickedUp: nextPickedUp,
          pickupStatus: updatePayload.pickupStatus,
        },
      });

      const manageAuctionItem = httpsCallable(functions, "manageAuctionItem");
      await manageAuctionItem({ eventId, auctionItemId: item.id, auctionAllocationId: allocation?.id || "", action: "set_pickup", pickedUp: nextPickedUp });

      showSuccess(
        nextPickedUp
          ? "✅ Item marked as picked up."
          : "✅ Item moved back to awaiting pickup/payment."
      );

      await loadAuctionResults();
    } catch (err) {
      logger.error("❌ Failed to update pickup status", {
        value: err,
      });

      showError("❌ Failed to update pickup status.");
    } finally {
      setSavingItemId("");
    }
  };

  const getFilteredItems = () => {
    if (statusFilter === "all") return auctionItems;

    if (statusFilter === "winners") {
      return auctionItems.filter((item) => Number(item.currentBid || 0) > 0);
    }

    if (statusFilter === "unpaid") {
      return auctionItems.filter(
        (item) =>
          Number(item.currentBid || 0) > 0 &&
          item.paymentStatus !== "paid"
      );
    }

    if (statusFilter === "paid") {
      return auctionItems.filter((item) => item.paymentStatus === "paid");
    }

    return auctionItems.filter((item) => item.status === statusFilter);
  };

  const filteredItems = getFilteredItems();

  const totalWinningBids = auctionItems.reduce((total, item) => {
    if (item.allocations?.length) {
      return total + item.allocations.reduce((sum, allocation) => sum + Number(allocation.amount || 0), 0);
    }
    return total + Number(item.currentBid || 0);
  }, 0);

  const totalPaid = auctionItems.reduce((total, item) => {
    if (item.allocations?.length) {
      return total + item.allocations
        .filter((allocation) => allocation.paymentStatus === "paid")
        .reduce((sum, allocation) => sum + Number(allocation.amount || 0), 0);
    }
    if (item.paymentStatus === "paid") {
      return total + Number(item.currentBid || 0);
    }

    return total;
  }, 0);

  const totalUnpaid = totalWinningBids - totalPaid;

    const formatDate = (value) => {
    const date = value?.toDate ? value.toDate() : value ? new Date(value) : null;

    if (!date || Number.isNaN(date.getTime())) {
      return "Not available";
    }

    return date.toLocaleString();
  };

  if (!ready || eventLoading) {
    return <FullPageSpinner message="Loading Auction Results..." />;
  }

  if (!auctionEnabled) {
    return (
      <AddonRequired
        eventslug={eventslug}
        title="Silent Auction Add-on Required"
        description="This event has not purchased the Silent Auction add-on yet."
      />
    );
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">🏆 Auction Results</h1>
          <p className="lead text-muted">
            Review winners, payments, and pickup status for silent auction items.
          </p>
        </div>
      </div>

      <div className="white-section p-4 rounded shadow-sm bg-white">
        {status && <div className="alert alert-success">{status}</div>}
        {error && <div className="alert alert-danger">{error}</div>}

        <div className="row g-3 mb-4">
          <div className="col-md-4">
            <div className="card border-success">
              <div className="card-body">
                <div className="text-muted small">Total Winning Bids</div>
                <h3 className="mb-0">{formatMoney(totalWinningBids)}</h3>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="card border-primary">
              <div className="card-body">
                <div className="text-muted small">Paid</div>
                <h3 className="mb-0">{formatMoney(totalPaid)}</h3>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="card border-warning">
              <div className="card-body">
                <div className="text-muted small">Unpaid</div>
                <h3 className="mb-0">{formatMoney(totalUnpaid)}</h3>
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <button
            className="btn btn-outline-secondary"
            onClick={loadAuctionResults}
            disabled={loading}
          >
            {loading ? "Refreshing..." : "🔄 Refresh Results"}
          </button>

          <select
            className="form-select"
            style={{ maxWidth: "240px" }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Auction Items</option>
            <option value="winners">Items With Winners</option>
            <option value="unpaid">Unpaid Winners</option>
            <option value="paid">Paid Winners</option>
            <option value="draft">Draft Items</option>
            <option value="open">Open Items</option>
            <option value="closed">Closed Items</option>
          </select>
        </div>

        {loading ? (
          <div className="alert alert-info">Loading auction results...</div>
        ) : filteredItems.length === 0 ? (
          <div className="alert alert-secondary">
            No auction results match the current filter.
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-striped align-middle">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Status</th>
                  <th>Winning Bid</th>
                  <th>Winner</th>
                  <th>Payment</th>
                  <th>Pickup</th>
                  <th style={{ minWidth: "220px" }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredItems.map((item) => {
                  const isSaving = savingItemId === item.id;
                  const hasWinner = Number(item.currentBid || 0) > 0;

                  return (
                    <tr key={item.id}>
                      <td>
                        <strong>{item.title || item.linkedEntryLabel}</strong>

                        {item.linkedEntryLabel && (
                          <div className="small text-muted">
                            Linked Entry: {item.linkedEntryLabel}
                          </div>
                        )}

                        {item.auctionNotes && (
                          <div className="small text-muted mt-1">
                            Notes: {item.auctionNotes}
                          </div>
                        )}

                        {hasWinner && (
                          <details className="mt-2">
                            <summary className="small text-primary" style={{ cursor: "pointer" }}>
                              Payment Audit
                            </summary>

                            <div className="small text-muted mt-2">
                              <div><strong>Order ID:</strong> {item.orderId || "Not created"}</div>
                              <div><strong>Stripe Session:</strong> {item.stripeCheckoutSessionId || "Not created"}</div>
                              <div><strong>Stripe Payment Intent:</strong> {item.stripePaymentIntentId || "Not available"}</div>
                              <div><strong>Payment Status:</strong> {item.paymentStatus || "not_required"}</div>
                              <div><strong>Pickup Status:</strong> {item.pickupStatus || (item.pickedUp ? "picked_up" : "not_picked_up")}</div>
                              <div><strong>Paid At:</strong> {formatDate(item.paidAt)}</div>
                              <div><strong>Payment Link Sent:</strong> {item.paymentLinkSent ? "Yes" : "No"}</div>
                              <div><strong>Payment Link Sent At:</strong> {formatDate(item.paymentLinkSentAt)}</div>
                              <div><strong>Winner Name:</strong> {item.currentWinnerName || "Not available"}</div>
                              <div><strong>Winner Email:</strong> {item.currentWinnerEmail || "Not available"}</div>
                            </div>
                          </details>
                        )}

                      </td>

                      <td>
                        <span
                          className={`badge ${
                            item.status === "open"
                              ? "bg-success"
                              : item.status === "closed"
                              ? "bg-secondary"
                              : "bg-warning text-dark"
                          }`}
                        >
                          {item.status || "draft"}
                        </span>
                      </td>

                      <td>
                        {hasWinner ? (
                          <strong>{formatMoney(item.currentBid)}</strong>
                        ) : (
                          <span className="text-muted">No bids</span>
                        )}
                      </td>

                      <td>
                        {item.allocations?.length ? (
                          <div className="d-grid gap-2">
                            {item.allocations.map((allocation) => (
                              <div key={allocation.id} className="border rounded p-2">
                                <strong>#{allocation.rank} {allocation.bidderName || "Auction Winner"}</strong>
                                <div className="small text-muted">{allocation.bidderEmail}</div>
                                <div className="small">{formatMoney(allocation.amount)}</div>
                                <span className={`badge ${allocation.paymentStatus === "paid" ? "bg-success" : "bg-warning text-dark"}`}>
                                  {allocation.paymentStatus || "unpaid"}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : item.currentWinnerName ? (
                          <>
                            <strong>{item.currentWinnerName}</strong>
                            {item.currentWinnerEmail && (
                              <div className="small text-muted">
                                {item.currentWinnerEmail}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-muted">None</span>
                        )}
                      </td>

                      <td>
                        {item.allocations?.length ? (
                          <span className="small text-muted">Tracked per winner</span>
                        ) : <select
                          className="form-select form-select-sm"
                          value={item.paymentStatus || "not_required"}
                          onChange={(e) =>
                            updatePaymentStatus(item.id, e.target.value)
                          }
                          disabled={isSaving || !hasWinner}
                        >
                          <option value="not_required">Not Required</option>
                          <option value="unpaid">Unpaid</option>
                          <option value="pending">Pending</option>
                          <option value="paid">Paid</option>
                          <option value="refunded">Refunded</option>
                        </select>}
                      </td>

                      <td>
                        {item.allocations?.length ? (
                          <span className="small text-muted">Tracked per winner</span>
                        ) : item.pickupStatus === "picked_up" || item.pickedUp === true ? (
                          <span className="badge bg-success">Picked Up</span>
                        ) : item.paymentStatus === "paid" ||
                          item.pickupStatus === "awaiting_pickup" ? (
                          <span className="badge bg-primary">Awaiting Pickup</span>
                        ) : (
                          <span className="badge bg-warning text-dark">
                            Awaiting Payment
                          </span>
                        )}
                      </td>

                      <td>
                        <div className="d-flex flex-wrap gap-2">
                          {item.allocations?.map((allocation) => (
                            <div key={allocation.id} className="border rounded p-2 w-100">
                              <div className="small fw-bold mb-2">Winner #{allocation.rank}</div>
                              <div className="d-flex flex-wrap gap-2 align-items-center">
                                <select
                                  className="form-select form-select-sm"
                                  style={{ maxWidth: "135px" }}
                                  value={allocation.paymentStatus || "unpaid"}
                                  disabled={savingItemId === `${item.id}:${allocation.id}`}
                                  onChange={(e) => updatePaymentStatus(item.id, e.target.value, allocation.id)}
                                >
                                  <option value="unpaid">Unpaid</option>
                                  <option value="pending">Pending</option>
                                  <option value="paid">Paid</option>
                                  <option value="refunded">Refunded</option>
                                </select>
                                {allocation.paymentStatus !== "paid" && (
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => sendAuctionPaymentLink(item, allocation)}
                                disabled={savingItemId === `${item.id}:${allocation.id}` || !allocation.bidderEmail}
                              >
                                {savingItemId === `${item.id}:${allocation.id}` ? "Sending..." : `Pay Link #${allocation.rank}`}
                              </button>
                                )}
                                <button
                                  className={allocation.pickupStatus === "picked_up" ? "btn btn-sm btn-outline-warning" : "btn btn-sm btn-outline-success"}
                                  onClick={() => togglePickedUp(item, allocation)}
                                  disabled={savingItemId === `${item.id}:${allocation.id}` || allocation.paymentStatus !== "paid"}
                                >
                                  {allocation.pickupStatus === "picked_up" ? "Undo Pickup" : "Mark Picked Up"}
                                </button>
                              </div>
                            </div>
                          ))}
                          {hasWinner && !item.allocations?.length && item.paymentStatus !== "paid" && (
                            <button
                              type="button"
                              className={
                                item.paymentLinkSent
                                  ? "btn btn-sm btn-outline-primary"
                                  : "btn btn-sm btn-primary"
                              }
                              onClick={() => sendAuctionPaymentLink(item)}
                              disabled={
                                isSaving ||
                                item.status !== "closed" ||
                                !item.currentWinnerEmail
                              }
                            >
                              {isSaving
                                ? "Sending..."
                                : item.paymentLinkSent
                                ? "💳 Resend Payment Link"
                                : "💳 Send Payment Link"}
                            </button>
                          )}
                          
                          {hasWinner && !item.allocations?.length &&
                            item.paymentStatus === "paid" &&
                            item.pickupStatus !== "picked_up" &&
                            item.pickedUp !== true && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-info"
                                onClick={() => sendPickupReminder(item)}
                                disabled={isSaving}
                              >
                                {isSaving ? "Sending..." : "📧 Pickup Reminder"}
                              </button>
                            )}

                          {!item.allocations?.length && <button
                            className={
                              item.pickedUp
                                ? "btn btn-sm btn-outline-warning"
                                : "btn btn-sm btn-outline-success"
                            }
                            onClick={() => togglePickedUp(item)}
                            disabled={isSaving || !hasWinner}
                          >
                            {item.pickedUp
                              ? "Mark Not Picked Up"
                              : "Mark Picked Up"}
                          </button>}

                          {hasWinner && !item.allocations?.length &&
                            item.paymentStatus !== "paid" && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => offerToNextBidder(item)}
                                disabled={isSaving}
                              >
                                ↪ Offer To Next Bidder
                              </button>
                            )}
                            
                          </div>

                        {hasWinner && item.status !== "closed" && item.paymentStatus !== "paid" && (
                          <div className="small text-muted mt-2">
                            Close item before sending payment.
                          </div>
                        )}

                        {hasWinner && !item.currentWinnerEmail && (
                          <div className="small text-danger mt-2">
                            Winner email missing.
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default AuctionResults;
